/**
 * Soft open/close animation for product + FAQ + cart note accordions (<details>).
 * Avoids close "jump" from outer margins by collapsing them with height.
 */
(function () {
  const DURATION = 380;
  const EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function getContent(details) {
    return (
      details.querySelector(':scope > .accordion__content-wrapper') ||
      details.querySelector(':scope > .accordion__content') ||
      details.querySelector(':scope > cart-note') ||
      details.querySelector(':scope > summary + *')
    );
  }

  function clearInline(content) {
    content.style.height = '';
    content.style.overflow = '';
    content.style.opacity = '';
    content.style.marginTop = '';
    content.style.marginBottom = '';
    content.style.paddingTop = '';
    content.style.paddingBottom = '';
    content.style.transition = '';
  }

  function finish(details, content, open) {
    if (details._accordionTimer) {
      clearTimeout(details._accordionTimer);
      details._accordionTimer = null;
    }
    if (!open) {
      details.removeAttribute('open');
    }
    clearInline(content);
    details.classList.remove('is-animating', 'is-opening', 'is-closing');
  }

  function animateOpen(details, content) {
    details.setAttribute('open', '');
    details.classList.add('is-animating', 'is-opening');
    details.classList.remove('is-closing');

    const endHeight = content.scrollHeight;

    content.style.overflow = 'hidden';
    content.style.height = '0px';
    content.style.opacity = '0';
    content.style.marginTop = '0px';
    content.style.marginBottom = '0px';
    content.style.paddingTop = '0px';
    content.style.paddingBottom = '0px';

    requestAnimationFrame(() => {
      content.style.transition =
        `height ${DURATION}ms ${EASING}, opacity ${DURATION}ms ease, ` +
        `margin-top ${DURATION}ms ${EASING}, margin-bottom ${DURATION}ms ${EASING}, ` +
        `padding-top ${DURATION}ms ${EASING}, padding-bottom ${DURATION}ms ${EASING}`;
      content.style.height = Math.max(endHeight, content.scrollHeight) + 'px';
      content.style.opacity = '1';
      content.style.marginTop = '';
      content.style.marginBottom = '';
      content.style.paddingTop = '';
      content.style.paddingBottom = '';
    });

    const onEnd = (event) => {
      if (event.target !== content || event.propertyName !== 'height') return;
      content.removeEventListener('transitionend', onEnd);
      finish(details, content, true);
    };
    content.addEventListener('transitionend', onEnd);
    details._accordionTimer = setTimeout(() => {
      content.removeEventListener('transitionend', onEnd);
      finish(details, content, true);
    }, DURATION + 80);
  }

  function animateClose(details, content) {
    details.classList.add('is-animating', 'is-closing');
    details.classList.remove('is-opening');

    const computed = window.getComputedStyle(content);
    const startHeight = content.scrollHeight;

    content.style.overflow = 'hidden';
    content.style.height = startHeight + 'px';
    content.style.opacity = '1';
    content.style.marginTop = computed.marginTop;
    content.style.marginBottom = computed.marginBottom;
    content.style.paddingTop = computed.paddingTop;
    content.style.paddingBottom = computed.paddingBottom;

    content.getBoundingClientRect();

    requestAnimationFrame(() => {
      content.style.transition =
        `height ${DURATION}ms ${EASING}, opacity ${Math.round(DURATION * 0.75)}ms ease, ` +
        `margin-top ${DURATION}ms ${EASING}, margin-bottom ${DURATION}ms ${EASING}, ` +
        `padding-top ${DURATION}ms ${EASING}, padding-bottom ${DURATION}ms ${EASING}`;
      content.style.height = '0px';
      content.style.opacity = '0';
      content.style.marginTop = '0px';
      content.style.marginBottom = '0px';
      content.style.paddingTop = '0px';
      content.style.paddingBottom = '0px';
    });

    const onEnd = (event) => {
      if (event.target !== content || event.propertyName !== 'height') return;
      content.removeEventListener('transitionend', onEnd);
      finish(details, content, false);
    };
    content.addEventListener('transitionend', onEnd);
    details._accordionTimer = setTimeout(() => {
      content.removeEventListener('transitionend', onEnd);
      finish(details, content, false);
    }, DURATION + 80);
  }

  function bindDetails(details) {
    if (details.dataset.accordionAnimate === 'true') return;
    const summary = details.querySelector(':scope > summary');
    const content = getContent(details);
    if (!summary || !content) return;

    details.dataset.accordionAnimate = 'true';

    summary.addEventListener('click', (event) => {
      if (prefersReducedMotion()) return;
      event.preventDefault();
      event.stopPropagation();

      // If a previous animation got stuck, force-finish then honor this click
      if (details.classList.contains('is-animating')) {
        const wasClosing = details.classList.contains('is-closing');
        finish(details, content, !wasClosing);
        if (wasClosing) return; // already closed
        animateClose(details, content);
        return;
      }

      if (details.open) {
        animateClose(details, content);
      } else {
        animateOpen(details, content);
      }
    });
  }

  function init(root) {
    const scope = root || document;
    scope
      .querySelectorAll('.accordion details, details.accordion, .collapsible-content details, .drawer__footer details')
      .forEach(bindDetails);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });

  document.addEventListener('cart-drawer:opened', () => init());

  const drawer = document.querySelector('cart-drawer');
  if (drawer && typeof MutationObserver !== 'undefined') {
    let scheduled = false;
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        init(drawer);
      });
    });
    observer.observe(drawer, { childList: true, subtree: true });
  }
})();
